
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n';
import { User } from '../types';
import { dbService } from '../services/dbService';
import { LogIn, UserPlus, Mail, Lock, User as UserIcon, Loader2, FlaskConical, Play } from 'lucide-react';

interface AuthViewProps {
  onLogin: (user: User) => void;
}

export const AuthView: React.FC<AuthViewProps> = ({ onLogin }) => {
  const { t } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [devMode, setDevMode] = useState(localStorage.getItem('lifebuckets-dev-mode') === 'true');

  const toggleDevMode = () => {
    const newState = !devMode;
    setDevMode(newState);
    localStorage.setItem('lifebuckets-dev-mode', String(newState));
  };

  const handleDevLogin = async () => {
    setIsLoading(true);
    // Force dev mode for this session
    localStorage.setItem('lifebuckets-dev-mode', 'true');
    const user = await dbService.login('dev_tester', 'password');
    if (user) onLogin(user);
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isLogin) {
        const user = await dbService.login(username.trim(), password.trim());
        if (user) {
          onLogin(user);
        } else {
          setError(t.invalidLogin);
        }
      } else {
        if (!email.trim()) {
          setError("Email is required");
          setIsLoading(false);
          return;
        }
        
        const avatar = `https://api.dicebear.com/7.x/avataaars/svg?seed=${username.trim()}`;
        const newUser = await dbService.signup({
          username: username.trim(),
          email: email.trim(),
          password: password.trim(),
          avatar
        });

        if (newUser) {
          onLogin(newUser);
        }
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f172a]">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 blur-[120px] rounded-full"></div>
      </div>

      <div className="w-full max-w-md animate-in fade-in zoom-in-95 duration-500 relative z-10">
        <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-2xl shadow-indigo-500/20 mx-auto mb-4">LB</div>
            <h1 className="text-3xl font-bold text-white mb-2">{t.appName}</h1>
            <p className="text-slate-400">{isLogin ? t.welcomeBack : t.getStarted}</p>
        </div>

        <div className="bg-slate-900/50 backdrop-blur-2xl border border-white/10 p-8 rounded-3xl shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">{t.username}</label>
              <div className="relative">
                <UserIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input 
                  type="text" 
                  value={username} 
                  required
                  disabled={isLoading}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-colors disabled:opacity-50"
                  placeholder="john_doe"
                />
              </div>
            </div>

            {!isLogin && (
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">{t.email}</label>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input 
                    type="email" 
                    value={email} 
                    required
                    disabled={isLoading}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-colors disabled:opacity-50"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">{t.password}</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input 
                  type="password" 
                  value={password} 
                  required
                  disabled={isLoading}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-colors disabled:opacity-50"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            <button 
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20 active:scale-95 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <>
                  {isLogin ? <LogIn size={20} /> : <UserPlus size={20} />}
                  {isLogin ? t.login : t.signup}
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-white/5 space-y-4">
            <p className="text-slate-400 text-sm text-center">
              {isLogin ? t.dontHaveAccount : t.alreadyHaveAccount}
              <button 
                onClick={() => setIsLogin(!isLogin)}
                disabled={isLoading}
                className="ml-2 text-indigo-400 font-bold hover:text-indigo-300 transition-colors disabled:opacity-50"
              >
                {isLogin ? t.signup : t.login}
              </button>
            </p>

            <div className="relative py-2">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/5"></div></div>
                <div className="relative flex justify-center text-[10px] uppercase tracking-widest font-bold text-slate-600"><span className="bg-slate-900 px-4">Local Preview</span></div>
            </div>

            <div className="flex flex-col gap-2">
                <button 
                    onClick={handleDevLogin}
                    disabled={isLoading}
                    className="w-full py-3 bg-white/5 hover:bg-white/10 text-slate-300 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
                >
                    <Play size={16} className="text-emerald-400" />
                    Try as Guest (Local Only)
                </button>

                <button 
                    onClick={toggleDevMode}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${devMode ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-slate-800/50 border-white/5 text-slate-500'}`}
                >
                    <div className="flex items-center gap-2">
                        <FlaskConical size={16} />
                        <span className="text-xs font-bold uppercase tracking-wider">Dev Mode {devMode ? 'ON' : 'OFF'}</span>
                    </div>
                    <div className={`w-8 h-4 rounded-full relative transition-colors ${devMode ? 'bg-amber-500' : 'bg-slate-700'}`}>
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${devMode ? 'left-4.5' : 'left-0.5'}`}></div>
                    </div>
                </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
